import React from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { Home, HeartHandshake, HelpCircle, CalendarDays, Menu, CalendarCheck, Users, ClipboardCheck, Bell, UserRound, ArrowLeft } from "lucide-react";
import { Logo } from "../Shared";

const FAMILY_TABS = [
  { to: "/app/family", icon: Home, label: "Home", end: true },
  { to: "/app/family/care", icon: HeartHandshake, label: "Care" },
  { to: "/app/family/requests", icon: HelpCircle, label: "Requests" },
  { to: "/app/family/calendar", icon: CalendarDays, label: "Calendar" },
  { to: "/app/family/more", icon: Menu, label: "More" },
];

const CM_TABS = [
  { to: "/app/cm", icon: CalendarCheck, label: "Today", end: true },
  { to: "/app/cm/families", icon: Users, label: "Families" },
  { to: "/app/cm/visits", icon: ClipboardCheck, label: "Visits" },
  { to: "/app/cm/alerts", icon: Bell, label: "Alerts" },
  { to: "/app/cm/profile", icon: UserRound, label: "Profile" },
];

export function AppHeader({ title, subtitle, back }) {
  const navigate = useNavigate();
  return (
    <header className="sticky top-0 z-40 border-b border-emerald-900 bg-emerald-950 text-white" style={{ paddingTop: "env(safe-area-inset-top)" }} data-testid="app-header">
      <div className="mx-auto flex h-14 max-w-lg items-center gap-3 px-4">
        {back && (
          <button onClick={() => navigate(-1)} className="-ml-1 rounded-full p-1.5 text-emerald-200 hover:bg-white/10" data-testid="app-header-back" aria-label="Back">
            <ArrowLeft className="h-5 w-5" />
          </button>
        )}
        {!back && <Logo light />}
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-bold tracking-tight" data-testid="app-header-title">{title}</p>
          {subtitle && <p className="truncate text-[11px] text-emerald-300">{subtitle}</p>}
        </div>
      </div>
    </header>
  );
}

function TabBar({ tabs, testid }) {
  return (
    <nav className="fixed bottom-0 left-0 right-0 z-40 border-t border-stone-200 bg-white" style={{ paddingBottom: "env(safe-area-inset-bottom)" }} data-testid={testid}>
      <div className="mx-auto flex max-w-lg justify-around py-1.5">
        {tabs.map((t) => (
          <NavLink key={t.to} to={t.to} end={t.end}
            className={({ isActive }) => `flex min-w-[56px] flex-col items-center gap-0.5 rounded-xl px-2 py-1.5 text-[10px] font-bold transition-colors ${isActive ? "text-emerald-900" : "text-stone-400"}`}
            data-testid={`tab-${t.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
            <t.icon className="h-5 w-5" /> {t.label}
          </NavLink>
        ))}
      </div>
    </nav>
  );
}

export function NativeFamilyShell() {
  return (
    <div className="min-h-screen bg-sand pb-24" data-testid="native-family-shell">
      <Outlet />
      <TabBar tabs={FAMILY_TABS} testid="native-family-tabbar" />
    </div>
  );
}

export function NativeCmShell() {
  return (
    <div className="min-h-screen bg-sand pb-24" data-testid="native-cm-shell">
      <Outlet />
      <TabBar tabs={CM_TABS} testid="native-cm-tabbar" />
    </div>
  );
}

export function NativeLegalShell({ title, children }) {
  return (
    <div className="min-h-screen bg-sand" data-testid="native-legal-shell">
      <AppHeader title={title} back />
      <main className="mx-auto max-w-lg px-4 py-5">{children}</main>
    </div>
  );
}
